/* eslint-disable max-lines-per-function */
import { useMutation } from "@apollo/client";
import { trim, get } from "lodash";
import moment from "moment";
import React from "react";
import { Modal } from "components/haven-design";
import { Select } from "components/haven-design/form/select";
import EditIcon from "@material-ui/icons/Edit";
import { apolloClient } from "data/graphql";
import {
  ChangeInterestStageProfile,
  ChangeInterestStageProfileVariables as ChangeVariables,
} from "types/gql-types";
import { InterestStage, STATUSES_REASON_LABEL } from "model/interest/status";
import { Input } from "components/haven-design/form/inputs";
import { Button } from "components/haven-design/buttons";
import StructuredReasons from "components/structured-reasons";
import { MoveInModal } from "pages/dashboard/interests-profile-slideout/components/modals/move-in";
import { CHANGE_STATUS } from "pages/dashboard/interests-profile/components/actions-query";
import { useSnackbar } from "components/haven-design/snackbar";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import TableContext from "../context";
import { useSidebarFilters } from "../sidebar-filters";
import tooltipStyles from "./tooltip.module.css";
import tableStyles from "./table.module.css";

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interest: any;
}


const SelectStage: React.FC<Props> = (props) => {
  const { status } = props.interest;
  const [selectedStage, setInterestStage] = React.useState(status);
  const [isEditing, toggleEditing] = React.useState(false);
  const [changeStage, mutationState] = useMutation<ChangeInterestStageProfile, ChangeVariables>(
    CHANGE_STATUS,
    {
      client: apolloClient,
    },
  );
  const [selectedStatus, setSelectedStatus] = React.useState<null | string>(null);
  const [reason, setReason] = React.useState<string>("");
  const { openSnackbar } = useSnackbar();
  const { search } = useSidebarFilters();

  if (isEditing) {
    return (
      <TableContext.Consumer>
        {(query) => {
          if (query === null || query.loading || query.error || !query.data) {
            return null;
          }

          const { data: { interestListsById: [interestList] } } = query;
          const { id: interestListId, property: { stages } } = interestList;

          const candidates = stages.filter(({ enabled }) => enabled).map(({ name }) => name);

          const handleChange = (stageValue: string | null) => () => {
            search();
            setInterestStage(stageValue);
            toggleEditing(false);
            openSnackbar({ message: "Success", status: "success" });
          };

          return (
            <>
              <Select
                value={selectedStage}
                disabled={mutationState.loading}
                onChange={({ target: { value } }) => {
                  if (
                    value === InterestStage.Removed ||
                    value === InterestStage.MovedIn ||
                    value === InterestStage.OnWaitlist
                  ) {
                    setSelectedStatus(value);
                    return;
                  }

                  changeStage({
                    variables: {
                      interestId: props.interest.id,
                      status: value,
                    },
                  }).then(handleChange(value));
                }}
                className="stage-select"
                name="stage"
              >
                {candidates.map((stage) => (
                  <option key={stage} value={stage}>
                    {stage}
                  </option>
                ))}
              </Select>

              <MoveInModal
                isOpen={selectedStatus === "Moved In"}
                onRequestClose={() => setSelectedStatus(null)}
                interestIds={[props.interest.id]}
                interestListId={interestListId}
              />

              <Modal
                title={
                  get(
                    STATUSES_REASON_LABEL,
                    selectedStatus || "",
                    "Please provide a reason for this change.",
                  )
                }
                isOpen={selectedStatus !== null && selectedStatus !== "Moved In"}
                onRequestClose={() => setSelectedStatus(null)}
                fullWidth
              >
                <DialogContent>
                  {selectedStatus !== "Removed" && (
                    <>
                      <p>
                        Changing the status to <strong>{selectedStatus}</strong> requires a reason
                        for auditing purposes.
                      </p>
                      <Input
                        type="text"
                        name="Reason"
                        label="Reason"
                        placeholder="Enter a reason..."
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                      />
                    </>
                  )}

                  {selectedStatus === "Removed" && (
                    <StructuredReasons
                      label="Removal Reason*"
                      onChange={setReason}
                    />
                  )}
                </DialogContent>
                <DialogActions>
                  <Button
                    color="secondary"
                    size="S"
                    onClick={() => {
                      toggleEditing(false);
                      setSelectedStatus(null);
                    }}
                  >
                      Cancel
                  </Button>
                  <Button
                    disabled={trim(reason) === "" || mutationState.loading}
                    color="primary"
                    size="S"
                    onClick={() => {
                      changeStage({
                        variables: {
                          interestId: props.interest.id,
                          status: selectedStatus!,
                          reason,
                        },
                      }).then(handleChange(selectedStatus));
                    }}
                  >
                      Confirm
                  </Button>
                </DialogActions>
              </Modal>
            </>
          );
        }}
      </TableContext.Consumer>
    );
  }

  const { lastStatusChange } = props.interest;
  const formatted = moment(lastStatusChange).format("h:mm A on MMMM D, YYYY");
  return (
    <div
      style={{
        cursor: "pointer",
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        paddingRight: "16px",
      }}
      onClick={() => toggleEditing(!isEditing)}
    >
      {lastStatusChange ? (
        <div className={tooltipStyles.tooltip} data-tooltip={formatted}>
          <div className={tableStyles.content} style={{ maxWidth: "194px" }}>
            {selectedStage}
          </div>
        </div>
      ) : (
        <div className={tableStyles.content} style={{ maxWidth: "114px" }}>
          {selectedStage}
        </div>
      )}
      <EditIcon
        style={{
          color: "#666",
          marginLeft: "4px",
          fontSize: "11px",
        }}
      />
    </div>
  );
};

export default SelectStage;
